/**
 * THE SHAPE OF THE WHOLE RECORDING, decoded in the browser.
 *
 * The player draws two or three arrangements against one time axis, and the
 * axis is easier to read when it carries the sound itself: a person looking for
 * "the bit where he stops talking" finds it in the envelope in a second and by
 * scrubbing in twenty.
 *
 * DECODED HERE AND NOWHERE ELSE. The manager never touches audio — that is the
 * worker's job and the reason uploads go straight to the bucket — so a server
 * that returned peaks would be a new audio dependency in the one process that
 * has none. `decodeAudioData` is a browser API, this page is the only browser
 * involved, and R9 ("all audio through the bus") governs `src/`, the PWA, where
 * two clips playing at once is a real bug. There is no bus here to route around.
 *
 * TWO REFUSALS, BOTH DELIBERATE:
 *
 *   · LENGTH. `decodeAudioData` inflates to 32-bit float PCM — nine hours at
 *     48 kHz is about six gigabytes, and the tab dies rather than reports.
 *     Past `MAX_DECODE_SEC` this returns `null` and the page draws a ruler.
 *   · CORS. The presigned URL points at the storage host, and a cross-origin
 *     `fetch` can only be READ if that host says so. Playback does not care —
 *     an `<audio>` element with no `crossOrigin` plays it regardless — so a
 *     bucket with no CORS policy costs the drawing and nothing else. That is
 *     exactly why this is best-effort and its failure is a note, not an error.
 */

/** Min/max per column. Two arrays rather than one of pairs: this is what a
 *  canvas loop wants and it halves the object count. */
export interface Peaks {
  min: Float32Array;
  max: Float32Array;
  /** The DECODED length, which is the honest one — `recordings.duration_sec`
   *  is whatever the uploader measured until the worker overwrites it. */
  durationSec: number;
}

/**
 * How many columns the envelope is computed at, ONCE, independent of how wide
 * the canvas happens to be.
 *
 * Recomputing on every resize would mean re-decoding or holding the whole
 * AudioBuffer alive; 2400 columns is finer than any laptop is wide, costs
 * 19 KB, and lets a redraw be a loop over an array.
 */
const BUCKETS = 2400;

/** Twenty minutes. Past this the decode is refused rather than attempted —
 *  see the header. A long session is still playable and still comparable. */
export const MAX_DECODE_SEC = 20 * 60;

/** And a second bound on the compressed side, because `durationSec` is only a
 *  claim until the worker has run. 96 MB of m4a is hours of speech. */
const MAX_DECODE_BYTES = 96 * 1024 * 1024;

export class WaveformUnavailable extends Error {}

/**
 * Fetch, decode, reduce to an envelope, and let the samples go.
 *
 * The AudioBuffer is dropped before returning: it is two orders of magnitude
 * bigger than the peaks and nothing else needs it. Same for the AudioContext,
 * which holds a hardware handle browsers cap at a handful per page.
 */
export async function loadPeaks(
  url: string,
  claimedDurationSec: number,
  signal: AbortSignal,
): Promise<Peaks> {
  if (claimedDurationSec > MAX_DECODE_SEC) {
    throw new WaveformUnavailable(
      `This recording is longer than ${Math.round(MAX_DECODE_SEC / 60)} minutes, so its waveform is not drawn. Everything else works.`,
    );
  }

  const Ctor =
    window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) throw new WaveformUnavailable('This browser cannot decode audio for drawing.');

  let bytes: ArrayBuffer;
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) throw new WaveformUnavailable(`Storage refused the audio (${res.status}).`);
    bytes = await res.arrayBuffer();
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    if (err instanceof WaveformUnavailable) throw err;
    /*
     * A blocked cross-origin read arrives as a bare `TypeError: Failed to
     * fetch`, indistinguishable from the network being down. Saying both is
     * more use than guessing one, and the audio playing underneath the message
     * is itself the clue that it is the read and not the file.
     */
    throw new WaveformUnavailable(
      'The browser could not read the audio bytes to draw them — the storage bucket sends no CORS header for this page. Playback and every lane still work.',
    );
  }

  if (bytes.byteLength > MAX_DECODE_BYTES) {
    throw new WaveformUnavailable('This file is too large to decode for drawing. Everything else works.');
  }

  const ctx = new Ctor();
  try {
    const buffer = await ctx.decodeAudioData(bytes);
    return envelope(buffer);
  } catch (err) {
    if (err instanceof WaveformUnavailable) throw err;
    throw new WaveformUnavailable('This audio could not be decoded for drawing. Everything else works.');
  } finally {
    void ctx.close().catch(() => {});
  }
}

/**
 * CHANNEL 0, not a mix. These are voice recordings from a phone and the second
 * channel, when there is one, is the same signal; averaging would double the
 * work of the only loop here that is O(samples) to move a pixel by nothing.
 */
function envelope(buffer: AudioBuffer): Peaks {
  const data = buffer.getChannelData(0);
  const min = new Float32Array(BUCKETS);
  const max = new Float32Array(BUCKETS);
  const per = data.length / BUCKETS;

  for (let b = 0; b < BUCKETS; b++) {
    const from = Math.floor(b * per);
    const to = b === BUCKETS - 1 ? data.length : Math.floor((b + 1) * per);
    let lo = 0;
    let hi = 0;
    for (let i = from; i < to; i++) {
      const v = data[i]!;
      if (v < lo) lo = v;
      else if (v > hi) hi = v;
    }
    min[b] = lo;
    max[b] = hi;
  }

  return { min, max, durationSec: buffer.duration };
}

/* ------------------------------------------------------------------ */
/* Drawing                                                             */
/* ------------------------------------------------------------------ */

/**
 * Paint the envelope, or a plain time ruler when there is none.
 *
 * COLOURS COME FROM THE STYLESHEET, read off the element. The palette lives in
 * `styles.css` by value already, having been copied there from the app's theme;
 * a third copy in TypeScript would be the one that drifts, and canvas is the
 * only place in this page that cannot just name a CSS variable.
 *
 * Sized in CSS pixels and scaled by `devicePixelRatio`, or the whole thing is
 * a blurry smear on every laptop made since 2012.
 */
export function drawTimeline(
  canvas: HTMLCanvasElement,
  peaks: Peaks | null,
  durationSec: number,
): void {
  const cssWidth = canvas.clientWidth;
  const cssHeight = canvas.clientHeight;
  if (cssWidth <= 0 || cssHeight <= 0) return;

  const dpr = window.devicePixelRatio || 1;
  const w = Math.round(cssWidth * dpr);
  const h = Math.round(cssHeight * dpr);
  if (canvas.width !== w) canvas.width = w;
  if (canvas.height !== h) canvas.height = h;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const style = getComputedStyle(canvas);
  const ink = style.getPropertyValue('--wave-ink').trim() || '#6b7688';
  const rule = style.getPropertyValue('--wave-rule').trim() || '#2b3342';

  ctx.clearRect(0, 0, w, h);
  const mid = h / 2;

  // The centre line is drawn either way: with peaks it is the zero crossing,
  // without them it is the axis the ticks hang off, so the strip never reads
  // as an empty box that failed to load.
  ctx.fillStyle = rule;
  ctx.fillRect(0, Math.round(mid), w, Math.max(1, Math.round(dpr)));

  if (peaks) {
    ctx.fillStyle = ink;
    const cols = peaks.min.length;
    for (let x = 0; x < w; x++) {
      const from = Math.floor((x / w) * cols);
      const to = Math.max(from + 1, Math.floor(((x + 1) / w) * cols));
      let lo = 0;
      let hi = 0;
      for (let b = from; b < to && b < cols; b++) {
        if (peaks.min[b]! < lo) lo = peaks.min[b]!;
        if (peaks.max[b]! > hi) hi = peaks.max[b]!;
      }
      const top = mid - hi * mid * 0.94;
      const bottom = mid - lo * mid * 0.94;
      ctx.fillRect(x, top, 1, Math.max(1, bottom - top));
    }
    return;
  }

  // No envelope: ticks, so the strip is still a readable time axis and still
  // worth dragging along.
  if (durationSec <= 0) return;
  const step = tickStep(durationSec);
  ctx.fillStyle = rule;
  for (let t = step; t < durationSec; t += step) {
    const x = Math.round((t / durationSec) * w);
    ctx.fillRect(x, h * 0.28, Math.max(1, Math.round(dpr)), h * 0.44);
  }
}

/** Round-numbered ticks, about a dozen of them whatever the length. */
function tickStep(durationSec: number): number {
  for (const step of [1, 2, 5, 10, 15, 30, 60, 120, 300, 600]) {
    if (durationSec / step <= 14) return step;
  }
  return 900;
}
